import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Bill, { generateBillPdf, uploadFile } from "./Bill";
import dateUtils from "utils/dateUtils";
/**
 * BookAppointmentModal Component
 *
 * Features:
 * - Prevents booking appointments for past dates
 * - Shows all time slots but grays out past ones for today's appointments
 * - Provides real-time validation and user feedback
 * - Silently prevents selection of past time slots without warning toasts
 * - Automatically updates available time slots based on current time
 */
import {
  Avatar,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Switch,
  Tag,
  TagLabel,
  Text,
  useToast,
  VStack,
} from "components-lib";
import { API_BASE_URL, API_ENDPOINTS, axiosInstance, getAuthHeaders } from "config/api";
import { AmbulanceIcon } from "hugeicons-react";

// docSignImage intentionally not imported here to avoid unused-import lint error
// Toggle verbose logging for bill PDF generation
// const LOG_BILL_PDF = true;
// Normalize backend base from env (fallback handled in config)
import { fetchAllDoctors, fetchDoctorsByClinic } from "services/doctorService";

const backendBase = API_BASE_URL.replace(/\/+$/g, "");
const apiRoot = `${backendBase}/api`;
const billsBaseUrl = `${apiRoot}/bills`;

// Unified payment states used across the component
const PaymentStates = {
  PAID: "PAID",
  PENDING: "PENDING",
};

const timeoutPromise = (ms, message) =>
  new Promise((_, reject) => setTimeout(() => reject(new Error(message)), ms));
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const BookAppointmentModal = ({
  isOpen,
  onClose,
  newAppointmentData,
  handleGenerateBill,
  billingSummary,
  onAddAppointmentSuccess,
  onPaymentStatusUpdate,
  setTableDataComplex,
  tableData,
  selectedPaymentRow,
} = {}) => {
  // --- Add: centralized initial state constants ---
  const INITIAL_APPOINTMENT_DATA = {
    doctorId: "",
    patientId: "",
    patientName: "",
    patientGender: "",
    patientAge: "",
    phone_no: "",
    serviceId: "",
    labReferred: "",
    appointmentDate: "",
    appointmentTime: "",
    appointmentType: "in_clinic",
    isEmergency: 0,
    isOutsideOPDEmergency: 0,
    doctor: "",
  };
  const INITIAL_BILL_SUMMARY = {
    grossAmount: 0,
    subtotal: 0,
    discountAmount: 0,
    emergencyCharge: 0,
    outsideOPDEmergencyChange: 0,
    taxAmount: 0,
    taxPercentage: 0,
    netAmount: 0,
    RecivedAmt: 0,
    PendingAmt: 0,
  };
  const INITIAL_SERVICE_DETAILS = {
    serviceId: "",
    unitPrice: "",
    discount: "",
  };

  // --- Update: useState initializers use constants ---
  const [isBillGenerated, setIsBillGenerated] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [serviceDetails, setServiceDetails] = useState(INITIAL_SERVICE_DETAILS);
  const [appointmentData, setAppointmentData] = useState(
    INITIAL_APPOINTMENT_DATA
  );
  const [billSummary, setBillSummary] = useState(INITIAL_BILL_SUMMARY);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [isLoggedInDoctor, SetisLoggedInDoctor] = useState(false);
  const [slotDuration, setSlotDuration] = useState();

  // Previously these were plain variables. Convert to state so React can track updates
  const [tokenPayloadState, setTokenPayloadState] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("tokenPayload") || "{}");
    } catch (e) {
      return {};
    }
  });

  const [doctorDataState, setDoctorDataState] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch (e) {
      return {};
    }
  });

  const [currentDoctorId, setCurrentDoctorId] = useState(() => {
    const dd = (() => {
      try {
        return JSON.parse(localStorage.getItem("user") || "{}");
      } catch (e) {
        return {};
      }
    })();
    if (dd.role === "front_desk") {
      return null;
    }
    return dd?.id || null;
  });

  const [isOwnerState, setIsOwnerState] = useState(() => {
    try {
      const payload = JSON.parse(localStorage.getItem("tokenPayload") || "{}");
      return payload?.ric?.toLowerCase() === "owner";
    } catch (e) {
      return false;
    }
  });
  const [currentDoctor, setcurrentDoctor] = useState({});
  const [currentDoctorName, setcurrentDoctorName] = useState();
  const [isFrontdesk, setisFrontdesk] = useState(
    JSON.stringify(localStorage.getItem("user")).role === "front_desk"
      ? true
      : false
  );
  const [frontdesk_id, setFrontdesk_id] = useState(
    JSON.stringify(localStorage.getItem("user")).role === "front_desk"
      ? JSON.stringify(localStorage.getItem("user")).id
      : null
  );
  const [clinicId, setclinicId] = useState();
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptError, setReceiptError] = useState("");
  const [receiptNote, setReceiptNote] = useState("");
  const [selectedDoctorDetails, setSelectedDoctorDetails] = useState(null);
  const [doctorServices, setDoctorServices] = useState([]);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [outsideOPDEmergency, setoutsideOPDEmergency] = useState(0);
  const [billSummaryVisible, setBillSummaryVisible] = useState(false);
  // For clinic header/doctor data
  // removed unused apidoctorData and apiClinicData states (Bill renders from appointmentData only)
  // Removed pdfBlob state; we generate and upload blobs directly when needed
  const [createdAppointmentId, setCreatedAppointmentId] = useState(null);
  const [createdBillId, setCreatedBillId] = useState(null);
  const [isBillViewOpen, setIsBillViewOpen] = useState(false);
  const [billPaymentNowSelection, setBillPaymentNowSelection] = useState("yes"); // 'yes' | 'no'
  // removed paymentCompleted in favor of unified paymentState
  const [appointmentPayload, setAppointmentPayload] = useState(null);
  const [appointmentID, setAppointmentID] = useState(null);
  // removed legacy payment pending flag state
  // Simplified payment state model
  const [paymentState, setPaymentState] = useState(null); // one of PaymentStates or null before decision
  const [ispaid, setispaid] = useState();
  const [ispending, setispending] = useState();
  const [bill_desp, setBillDesp] = useState("");
  const toast = useToast();

  // Dynamic role/state refs
  const [isAdmin, setIsAdmin] = useState(false);
  const isAdminRef = useRef(false);
  const toastNoRef = useRef(false);
  // Receipt upload state: each item { name, url, file }
  const [receiptItems, setReceiptItems] = useState([]);
  const [receiptUploading, setReceiptUploading] = useState(false);

  // --- Add: reset helper that restores popup-local state to initial values ---
  const resetState = () => {
    setIsBillGenerated(false);
    setSelectedServices([]);
    toastNoRef.current = false;
    setServiceDetails(INITIAL_SERVICE_DETAILS);
    setAppointmentData(INITIAL_APPOINTMENT_DATA);
    setBillSummary(INITIAL_BILL_SUMMARY);
    setAvailableTimeSlots([]);
    setIsReceiptModalOpen(false);
    setReceiptFile(null);
    setReceiptError("");
    setSelectedDoctorDetails(null);
    setDoctorServices([]);
    setIsLoadingServices(false);
    setoutsideOPDEmergency(0);
    setBillSummaryVisible(false);
    setCreatedAppointmentId(null);
    setCreatedBillId(null);
    setIsBillViewOpen(false);
    setBillPaymentNowSelection(false);
    setAppointmentPayload(null);
    setAppointmentID(null);
    setPaymentState(null);
    setispaid(undefined);
    setispending(undefined);
    setBillDesp("");
    setReceiptItems([]);
    setReceiptUploading(false);
    // Note: doctors list intentionally preserved (global/populated list)
  };

  // Reset popup-local state when modal opens or closes.
  // - On open: ensure a fresh "Book Appointment" view and re-apply any newAppointmentData prefill.
  // - On close: fully reset popup-local state (doctors list preserved).
  useEffect(() => {
    if (isOpen) {
      // fresh state when opened
      resetState();
      if (newAppointmentData) {
        // re-apply incoming prefill after reset (keeps fields minimal)
        setAppointmentData((prev) => ({
          ...INITIAL_APPOINTMENT_DATA,
          patientId:
            newAppointmentData.patientId ||
            newAppointmentData.patient ||
            newAppointmentData.patient_id ||
            "",
          patientName:
            newAppointmentData.patientName || newAppointmentData.name || "",
          patientGender:
            newAppointmentData.patientGender || newAppointmentData.gender || "",
          patientAge:
            newAppointmentData.patientAge || newAppointmentData.age || "",
          phone_no:
            newAppointmentData.phone_no || newAppointmentData.phoneNumber || "",
          appointmentDate: newAppointmentData.appointmentDate || "",
          appointmentType: newAppointmentData.appointmentType || "in_clinic",
          isEmergency: newAppointmentData.isEmergency ? 1 : 0,
          isOutsideOPDEmergency: newAppointmentData.isOutsideOPDEmergency
            ? 1
            : 0,
        }));
      } else {
        setAppointmentData(INITIAL_APPOINTMENT_DATA);
      }
    } else {
      // closed -> clear everything
      resetState();
    }
    // include newAppointmentData so reopening with different prefill works
    // If modal opened and appointmentData contains doctor/date, fetch details/slots
  }, [isOpen, newAppointmentData]);

  // Clear receiptItems when bill summary UI closed or when modal fully closed
  useEffect(() => {
    if (!billSummaryVisible) {
      // user closed bill summary panel -> clear uploaded receipt links
      setReceiptItems([]);
    }
  }, [billSummaryVisible]);

  // Ensure doctor services and slots are populated when modal opens or when doctor/date changes
  useEffect(() => {
    if (!isOpen) return;
    const doctorId = appointmentData?.doctorId;
    const date = appointmentData?.appointmentDate;
    if (isFrontdesk) return;
    if (doctorId) {
      // fetch doctor profile/services
      try {
        fetchDoctorDetails(doctorId);
      } catch (e) {
        console.warn("Failed to fetch doctor details on open", e);
      }
    }
    if (doctorId && date) {
      try {
        fetchTimeSlots(doctorId, date);
      } catch (e) {
        console.warn("Failed to fetch time slots on open", e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // --- Add: handleClose wrapper to ensure reset occurs immediately when parent triggers onClose ---
  const handleClose = () => {
    try {
      resetState();
    } catch (e) {
      // swallow - best effort reset
      console.warn("resetState failed:", e);
    }
    if (typeof onClose === "function") onClose();
  };

  const postJsonWithRetry = async (
    url,
    data,
    { headers = {}, timeout = 15000 } = {},
    attempts = 3,
    initialDelay = 800
  ) => {
    let attempt = 0;
    let lastErr;
    while (attempt < attempts) {
      try {
        return await axios.post(url, data, { headers, timeout });
      } catch (err) {
        lastErr = err;
        attempt += 1;
        const status = err?.response?.status;
        if (status && status >= 400 && status < 500) break;
        await sleep(initialDelay * Math.pow(2, attempt - 1));
      }
    }
    throw lastErr;
  };

  const uploadReceiptsandBill = async ({
    billId,
    invoiceUrl,
    receiptUrls = [],
  }) => {
    if (!billId) throw new Error("Missing bill id for upload sync.");
    if (!invoiceUrl && receiptUrls.length === 0) return null;
    const authToken =
      localStorage.getItem("token") || localStorage.getItem("Token") || "";
    const payload = { bill_id: billId };
    if (invoiceUrl) payload.bill_invoice_url = invoiceUrl;
    if (receiptUrls.length) payload.payment_receipt_url = receiptUrls;
    const headers = {
      "Content-Type": "application/json",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    };
    return postJsonWithRetry(`${billsBaseUrl}/uploadReceipt`, payload, {
      headers,
      timeout: 20000,
    });
  };

  // Upload local File objects to backend and store returned object URLs in receiptItems
  const uploadFilesAndSetUrls = async (files = []) => {
    if (!files || files.length === 0) return [];
    setReceiptUploading(true);
    try {
      // Prefer uploadFile exported from Bill if available (may handle storage specifics)
      const results = await Promise.all(
        files.map(async (file) => {
          try {
            if (typeof uploadFile === "function") {
              const url = await uploadFile(file, {
                fileName: file.name,
                mimeType: file.type,
              });
              return { name: file.name, url, file };
            }
            // Fallback: POST multipart to api/upload
            const form = new FormData();
            form.append("file", file);
            const resp = await axiosInstance.post("api/upload", form, {
              headers: { "Content-Type": "multipart/form-data" },
              maxBodyLength: Infinity,
            });
            const data = resp?.data;
            const url =
              data?.objectUrl ||
              data?.url ||
              data?.fileUrl ||
              (Array.isArray(data) ? data[0] : null);
            return { name: file.name, url, file };
          } catch (err) {
            console.warn("Single file upload failed:", err);
            return null;
          }
        })
      );
      const ok = results.filter(Boolean);
      // Keep only name + url in state to avoid re-uploading File objects on Save
      const okSlim = ok
        .map((r) => ({ name: r.name, url: r.url }))
        .filter((r) => r && (r.url || r.name));

      // Merge with existing receiptItems and dedupe by url or name
      setReceiptItems((prev) => {
        const merged = [...(Array.isArray(prev) ? prev : []), ...okSlim];
        const seen = new Set();
        return merged.filter((it) => {
          const key = it.url || it.name;
          if (!key) return false;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      });

      return ok.map((r) => r.url).filter(Boolean);
    } finally {
      setReceiptUploading(false);
    }
  };

  const removeReceiptItem = (index) => {
    setReceiptItems((prev) => {
      const next = Array.isArray(prev) ? prev.slice() : [];
      next.splice(index, 1);
      return next;
    });
  };

  // Save or update bill resiliently: on 409 fetch latest and retry once.
  // const saveOrUpdateBill = async (billId, payload) => {
  //   if (!billId) throw new Error("Missing bill id");
  //   const url = `${billsBaseUrl}/update/${billId}`;
  //   try {
  //     const resp = await axiosInstance.put(url, payload);
  //     return resp.data;
  //   } catch (err) {
  //     const status = err?.response?.status;
  //     if (status === 409) {
  //       // Conflict: fetch latest bill, merge minimally and retry once
  //       try {
  //         const latestResp = await axiosInstance.get(
  //           `${billsBaseUrl}/${billId}`,
  //         );
  //         const latest = latestResp?.data || {};
  //         // Merge: prefer payload fields but keep any server-only fields
  //         const merged = { ...latest, ...payload };
  //         const retryResp = await axiosInstance.put(url, merged);
  //         toast({
  //           title: "Bill updated (resolved conflict)",
  //           status: "info",
  //           duration: 3000,
  //           isClosable: true,
  //         });
  //         return retryResp.data;
  //       } catch (e2) {
  //         console.error("Retry after 409 failed", e2);
  //         throw e2;
  //       }
  //     }
  //     throw err;
  //   }
  // };

  // Get today's date in YYYY-MM-DD format for date validation
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Get current time in HH:MM format with buffer for booking
  const getCurrentTime = () => {
    const now = new Date();
    // Add 10 minutes buffer to current time to allow reasonable booking window
    now.setMinutes(now.getMinutes() + 10);
    return `${String(now.getHours()).padStart(2, "0")}:${String(
      now.getMinutes()
    ).padStart(2, "0")}`;
  };

  // Get current time without buffer for display purposes
  const getCurrentTimeExact = () => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, "0")}:${String(
      now.getMinutes()
    ).padStart(2, "0")}`;
  };

  // Get minimum selectable date (today)
  const minSelectableDate = getTodayDate();

  // Helper function to check if a date is in the past
  const isDateInPast = (dateString) => {
    if (!dateString) return false;
    return dateString < minSelectableDate;
  };

  // Helper function to check if a time slot is in the past for today's date
  const isTimeSlotInPast = (timeString, selectedDate) => {
    if (!timeString || !selectedDate) return false;

    // Only check for past times if the selected date is today
    if (selectedDate === minSelectableDate) {
      const currentTimeWithBuffer = getCurrentTime();
      // Convert time strings to minutes for accurate comparison
      const [currentHours, currentMinutes] = currentTimeWithBuffer
        .split(":")
        .map(Number);
      const [slotHours, slotMinutes] = timeString.split(":").map(Number);

      const currentTotalMinutes = currentHours * 60 + currentMinutes;
      const slotTotalMinutes = slotHours * 60 + slotMinutes;

      // Return true if slot time is before current time + buffer
      return slotTotalMinutes <= currentTotalMinutes;
    }

    return false;
  };

  // Helper: convert 24-hour HH:MM to 12-hour display (e.g. 13:30 -> 01:30 PM)
  const formatTime12h = (timeString) => {
    if (!timeString) return "";
    const [h, m] = timeString.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const hr = h % 12 === 0 ? 12 : h % 12;
    return `${String(hr).padStart(2, "0")}:
${String(m).padStart(2, "0")} ${ampm}`.replace(/\n/, "");
  };

  // Generate a full, deterministic list of time slots to always show in the select.
  // Default: 08:00 to 20:00 in 30 minute steps. This ensures the UI always shows
  // the common clinic slots even when the API returns a subset.
  const generateAllTimeSlots = (start = "00:00", end = "24:00") => {
    const out = [];
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    let cur = new Date();
    cur.setHours(sh, sm, 0, 0);
    const endDate = new Date();
    endDate.setHours(eh, em, 0, 0);
    while (cur <= endDate) {
      out.push(
        `${String(cur.getHours()).padStart(2, "0")}:${String(
          cur.getMinutes()
        ).padStart(2, "0")}`
      );
      cur = new Date(cur.getTime() + slotDuration * 60 * 1000);
    }
    return out;
  };

  const ALL_TIME_SLOTS = generateAllTimeSlots();

  // let clinicId;

  // Time slot formatting is handled where slots are mapped for display
  useEffect(() => {
    // Read the tokenPayload and user from localStorage and populate state
    let tp = {};
    let ud = {};
    try {
      tp = JSON.parse(localStorage.getItem("tokenPayload") || "{}");
    } catch (e) {
      tp = {};
    }
    try {
      ud = JSON.parse(localStorage.getItem("user") || "{}");
    } catch (e) {
      ud = {};
    }

    setTokenPayloadState(tp);
    setDoctorDataState(ud);

    if (ud.role === "front_desk") {
      setFrontdesk_id(ud.id);
    }

    SetisLoggedInDoctor(ud.role && String(ud.role).toLowerCase() === "doctor");

    const adminFlag = tp.role?.toLowerCase() === "admin";
    setIsAdmin(adminFlag);
    isAdminRef.current = adminFlag;

    setIsOwnerState(tp.ric?.toLowerCase() === "owner");

    setclinicId(ud.clinic_id);

    // Set the current doctor id from the user record (if present)
    const ddId = ud.role === "front_desk" ? null : ud?.id;
    setCurrentDoctorId(ddId);

    setcurrentDoctorName(ud.doctor || "Current Doctor");

    // Pre-select doctor id in appointmentData if available
    if (ddId) {
      setAppointmentData((prevData) => ({
        ...prevData,
        doctorId: String(ddId),
      }));
    }
  }, []);

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        // If logged in as a doctor, set only current doctor and auto-select
        let doctorsList = [];
        if (isAdmin) {
          doctorsList = await fetchAllDoctors({ force: false });
        } else if (clinicId) {
          doctorsList = await fetchDoctorsByClinic(clinicId, { force: false });
        } else {
          return;
        }

        setDoctors(doctorsList || []);
      } catch (error) {
        console.error("Error fetching doctors:", error);
        setDoctors([]);
      }
    };
    if (isLoggedInDoctor) {
      const LoggedIncurrentDoctor = {
        id: currentDoctorId,
        doctor: currentDoctorName,
      };
      setcurrentDoctor(LoggedIncurrentDoctor);
      setDoctors([LoggedIncurrentDoctor]);
      setAppointmentData((prevData) => ({
        ...prevData,
        doctorId: currentDoctorId,
      }));

      return;
    }
    fetchDoctors();
  }, [isLoggedInDoctor, isAdmin, clinicId, currentDoctorId, currentDoctorName]);

  // useEffect(() => {
  //   if (
  //     isLoggedInDoctor &&
  //     appointmentData.doctorId &&
  //     appointmentData.appointmentDate
  //   ) {
  //     // Trigger the doctor change logic to load schedule
  //     const fakeEvent = {
  //       target: { name: "doctorId", value: appointmentData.doctorId },
  //     };
  //     handleInputChange(fakeEvent);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [
  //   appointmentData.doctorId,
  //   appointmentData.appointmentDate,
  //   isLoggedInDoctor,
  // ]);

  // Fetch doctor details when doctorId changes
  // useEffect(() => {
  //   if (appointmentData.doctorId) {
  //     fetchDoctorDetails(appointmentData.doctorId);
  //   } else {
  //     // Clear services when no doctor is selected
  //     setDoctorServices([]);
  //     setServiceDetails({ serviceId: "", unitPrice: "", discount: "" });
  //     setIsLoadingServices(false);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [appointmentData.doctorId]);

  // // When the logged-in user is a doctor, auto-fetch their profile/services
  // useEffect(() => {
  //   if (isLoggedInDoctor && appointmentData.doctorId) {
  //     try {
  //       fetchDoctorDetails(appointmentData.doctorId);
  //     } catch (e) {
  //       console.warn('Auto fetch doctor details failed', e);
  //     }
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isLoggedInDoctor, appointmentData.doctorId]);

  // // When we have a currentDoctorId (loaded from localStorage) fetch doctor details
  // useEffect(() => {
  //   if (!isOpen) return;
  //   if (!currentDoctorId) return;
  //   try {
  //     // currentDoctorId may be a number or string
  //     const id = typeof currentDoctorId === "number" ? currentDoctorId : parseInt(currentDoctorId);
  //     if (!id) return;
  //     // populate appointmentData doctorId if not already set
  //     setAppointmentData((prev) => ({ ...prev, doctorId: String(id) }));
  //     // fetch details and slots for today's date if appointmentDate is present
  //     fetchDoctorDetails(id);
  //     if (appointmentData.appointmentDate) {
  //       fetchTimeSlots(id, appointmentData.appointmentDate);
  //     }
  //   } catch (e) {
  //     console.warn("Failed to auto-fetch doctor details for currentDoctorId", e);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [currentDoctorId, isOpen]);

  useEffect(() => {
    // Determine which doctor ID we should use
    let id = currentDoctorId;

    // Normalize to a number
    if (typeof id === "string") id = parseInt(id);

    // Guard clauses: if no ID or not open, exit early
    if (!id || !isOpen) return;

    // If logged-in doctor, ensure appointmentData reflects that
    if (isLoggedInDoctor && !appointmentData.doctorId) {
      setAppointmentData((prev) => ({ ...prev, doctorId: String(id) }));
    }

    // Fetch only once per unique ID
    let cancelled = false;
    const fetch = async () => {
      try {
        if (!id) return;
        await fetchDoctorDetails(id);
        if (appointmentData.appointmentDate) {
          await fetchTimeSlots(id, appointmentData.appointmentDate);
        }
      } catch (e) {
        console.warn("Failed to fetch doctor details", e);
      }
    };
    fetch();

    return () => {
      cancelled = true;
    };
  }, [appointmentData.doctorId, currentDoctorId, isLoggedInDoctor, isOpen]);

  useEffect(() => {
    handleGenerateBillClick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentData.isEmergency, serviceDetails]);

  useEffect(() => {
    if (newAppointmentData) {
      console.log("=== BOOK APPOINTMENT MODAL - NEW APPOINTMENT DATA ===");
      console.log("newAppointmentData:", newAppointmentData);
      console.log(
        "patientId from newAppointmentData.patientId:",
        newAppointmentData.patientId
      );
      console.log(
        "patientId from newAppointmentData.patient:",
        newAppointmentData.patient
      );
      console.log(
        "patientId from newAppointmentData.patient_id:",
        newAppointmentData.patient_id
      );
      console.log("=== END BOOK APPOINTMENT MODAL DATA ===");

      setAppointmentData((prevData) => ({
        ...prevData,
        patientId:
          newAppointmentData.patientId ||
          newAppointmentData.patient ||
          newAppointmentData.patient_id ||
          "",
        patientName:
          newAppointmentData.patientName || newAppointmentData.name || "",
        patientGender:
          newAppointmentData.patientGender || newAppointmentData.gender || "",
        patientAge:
          newAppointmentData.patientAge || newAppointmentData.age || "",
        phone_no:
          newAppointmentData.phone_no || newAppointmentData.phoneNumber || "",
        appointmentDate: newAppointmentData.appointmentDate || "",
        appointmentType: newAppointmentData.appointmentType || "in_clinic",
        isEmergency: newAppointmentData.isEmergency ? 1 : 0,
        isOutsideOPDEmergency: newAppointmentData.isOutsideOPDEmergency ? 1 : 0,
      }));

      // If it's an emergency appointment, automatically set the emergency switch
      if (newAppointmentData.isEmergency) {
        setAppointmentData((prevData) => ({
          ...prevData,
          isEmergency: 1,
        }));
      }
      // If it's an  OutsideOPDEmergency appointment, automatically set the OutsideOPDEmergency switch
      if (newAppointmentData.isEmergency) {
        setAppointmentData((prevData) => ({
          ...prevData,
          OutsideOPDEmergency: 1,
        }));
      }
    }
  }, [newAppointmentData]);

  // Handle time slot selection with validation - silently prevent past slot selection
  const handleTimeSlotSelection = (selectedTime) => {
    // Silently prevent selection of past time slots for today without showing warning
    if (isTimeSlotInPast(selectedTime, appointmentData.appointmentDate)) {
      return false; // Don't update the state, no toast notification
    }

    // Update appointment data with selected time
    setAppointmentData((prevData) => ({
      ...prevData,
      appointmentTime: selectedTime,
    }));

    // Determine if selected time exists in availableTimeSlots (by value)
    const isAvailable = availableTimeSlots.some(
      (s) => s.value === selectedTime && !s.isPast
    );

    // If selected slot is not available, mark as outside OPD emergency
    // setAppointmentData((prevData) => ({
    //   ...prevData,
    //   isOutsideOPDEmergency: isAvailable ? 0 : 1,
    // }));

    // setoutsideOPDEmergency(isAvailable ? 0 : 1);

    return isAvailable;
  };

  const handleInputChange = async (e) => {
    const { name, value } = e.target;

    // Handle time slot selection with special validation
    if (name === "appointmentTime") {
      // If user selected a time, update via helper which will set the outsideOPD flag
      if (appointmentData.isEmergency === 1) {
        return;
        // toast({
        //     title: "Emergency Appointment",
        //     description:
        //         "Cannot change time slot for emergency appointments.",
        //     status: "warning",
        //     duration: 2500,
        //     isClosable: true,
        // }
      }
      const ok = handleTimeSlotSelection(value);
      if (!ok) {
        // If slot was past, silently ignore (no state change)
        if (isTimeSlotInPast(value, appointmentData.appointmentDate)) return;
        // If slot was outside OPD (not in available list), show info to user
        const found = availableTimeSlots.find((s) => s.value === value);

        if (!found && appointmentData.isEmergency === 0) {
          handleTimeSlotSelection(value);
          setAppointmentData((prevData) => ({
            ...prevData,
            isOutsideOPDEmergency: 1,
          }));
          setoutsideOPDEmergency(1);
          toast({
            title: "Outside OPD",
            description:
              "This time is outside normal OPD. Booking will be marked as Outside OPD.",
            status: "warning",
            duration: 2500,
            isClosable: true,
          });
          return;
        }
      }
      if (appointmentData.isEmergency === 0) {
        setAppointmentData((prevData) => ({
          ...prevData,
          isOutsideOPDEmergency: 0,
        }));
      }
      setoutsideOPDEmergency(0);
      return;
    }

    if (name === "doctorId") {
      try {
        // Get the selected date for fetching slots
        const selectedDate =
          appointmentData.appointmentDate ||
          new Date().toISOString().split("T")[0];
        console.log("Doctor selected:", value, "for date:", selectedDate);

        setcurrentDoctor(doctors.find((doc) => doc.id === parseInt(value)));
        // setAppointmentData((prevData) => ({
        //   ...prevData,
        //   doctorId: value,
        // }));
        // Fetch doctor details including emergency charge
        await fetchDoctorDetails(value);

        // Fetch available time slots for the selected doctor and date
        await fetchTimeSlots(value, selectedDate);
      } catch (error) {
        console.error("Error in doctor selection:", error);
        toast({
          title: "Error",
          description: "Failed to fetch doctor's available slots",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }

    if (name === "appointmentDate") {
      const selectedDate = value;
      console.log("Date changed to:", selectedDate);

      // Validate that the selected date is not in the past
      if (isDateInPast(selectedDate)) {
        toast({
          title: "Invalid Date",
          description:
            "Please select a date from today onwards. Past dates are not allowed.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return; // Don't update the state with past date
      }

      if (appointmentData.doctorId) {
        try {
          // Fetch available time slots for the selected doctor and new date
          await fetchTimeSlots(appointmentData.doctorId, selectedDate);
        } catch (error) {
          console.error("Error handling date change:", error);
          toast({
            title: "Error",
            description:
              "Failed to fetch available slots for the selected date",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      }
    }

    setAppointmentData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const fetchDoctorDetails = async (doctorId) => {
    try {
      setIsLoadingServices(true);
      console.log("Fetching doctor details for ID:", doctorId);
      const response = await axiosInstance.get(
        `/api/doctor/profile?doctor_id=${doctorId}`
      );
      console.log("Doctor details response:", response.data);
      console.log(
        "Emergency charge from doctor details:",
        response.data?.doctor?.emergency_charge
      );

      // Extract and set doctor services from the API response
      const doctorData = response.data?.doctor || response.data;
      const clinicData = response.data?.clinic || response.data;
      console.log("Doctor data from API:", doctorData);
      console.log("Clinic data from API:", clinicData);
      // doctor/clinic data are not stored in state for Bill anymore
      // Parse comma-separated services, unit_price, and discount strings
      const serviceNames = doctorData?.service
        ? doctorData.service.split(",").map((s) => s.trim())
        : [];
      const unitPrices = doctorData?.unit_price
        ? doctorData.unit_price.split(",").map((p) => p.trim())
        : [];
      const discounts = doctorData?.discount
        ? doctorData.discount.split(",").map((d) => d.trim())
        : [];

      console.log("Parsed service data:", {
        serviceNames,
        unitPrices,
        discounts,
      });

      // Validate data consistency
      if (
        serviceNames.length !== unitPrices.length ||
        serviceNames.length !== discounts.length
      ) {
        console.warn("Service data arrays have mismatched lengths:", {
          services: serviceNames.length,
          prices: unitPrices.length,
          discounts: discounts.length,
        });
      }

      // Map services with their corresponding prices and discounts
      const formattedServices = serviceNames.map((serviceName, index) => ({
        id: index + 1,
        name: serviceName,
        unitPrice: unitPrices[index] || "0",
        discount: discounts[index] || "0",
      }));

      console.log("Formatted services for dropdown:", formattedServices);
      setDoctorServices(formattedServices);

      // Show appropriate message based on services availability
      if (formattedServices.length > 0) {
        // toast({
        //   title: "Services Loaded",
        //   description: `${formattedServices.length} service(s) available for this doctor`,
        //   status: "success",
        //   duration: 2000,
        //   isClosable: true,
        // });
      } else {
        toast({
          title: "No Services Found",
          description:
            "This doctor has no services configured. Please contact admin.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
      }
      setSlotDuration(doctorData.slot_duration);
      setSelectedDoctorDetails(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching doctor details:", error);
      setSelectedDoctorDetails(null);
      setDoctorServices([]); // Clear services on error

      toast({
        title: "Error Loading Services",
        description: "Failed to load doctor services. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });

      return null;
    } finally {
      setIsLoadingServices(false);
    }
  };

  const fetchTimeSlots = async (doctorId, selectedDate) => {
    console.log(
      "Fetching time slots for doctor:",
      doctorId,
      "date:",
      selectedDate
    );
    if (!doctorId || !selectedDate) {
      console.log("Missing required data:", { doctorId, selectedDate });
      return;
    }

    try {
      // Call the API to get slots for the doctor
      const response = await axiosInstance.get(
        `${API_BASE_URL}api/doctor/getSlots`,
        {
          params: {
            id: doctorId,
            date: selectedDate,
          },
        }
      );

      console.log("API response for slots:", response.data);

      // Handle the API response format: { slots: ["11:00", "11:50", "12:40", "13:30"] }
      const slotsData = response.data.slots || response.data || [];

      // Include all slots but mark past ones for today's date
      console.log("All available slots from API:", slotsData);

      // Convert time strings to proper format with display values and past indication
      const availableSlots = slotsData.map((timeString) => {
        // Convert 24-hour format to 12-hour format for display
        const [hours, minutes] = timeString.split(":").map(Number);
        const ampm = hours >= 12 ? "PM" : "AM";
        const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
        const displayTime = `${String(displayHours).padStart(2, "0")}:${String(
          minutes
        ).padStart(2, "0")} ${ampm}`;

        const isPast = isTimeSlotInPast(timeString, selectedDate);

        return {
          value: timeString, // Keep original 24-hour format for API calls
          display: displayTime, // Show 12-hour format to user
          isPast: isPast, // Track if this slot is in the past
          isDisabled: isPast, // Disable past slots for today
        };
      });

      console.log("Available slots with past indication:", availableSlots);

      setAvailableTimeSlots(availableSlots);
      // toastNoRef.current = false;
      // Only show warning if no slots are available at all
      if (availableSlots.length === 0 && !toastNoRef.current) {
        // setoutsideOPDEmergency(1);
        // setAppointmentData((prev) => ({
        //   ...prev,
        //   isOutsideOPDEmergency: 1, // Set OutsideOPDemergency if no slots,
        // }));
        toast({
          title: "No OPD Slots",
          description: "Please Select Outside OPD ",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        toastNoRef.current = true;
        return;
      } else {
        setoutsideOPDEmergency(0);
        setAppointmentData((prev) => ({
          ...prev,
          isOutsideOPDEmergency: 0, // Set OutsideOPDEmergency to 0 if slots are available
        }));
      }
    } catch (error) {
      console.error("Error fetching time slots:", error);
      setAvailableTimeSlots([]);
      toast({
        title: "Error",
        description:
          "Failed to fetch available time slots: " +
          (error.response?.data?.message || error.message),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleGenerateBillClick = () => {
    let grossAmount = 0;
    let discountAmount = 0;
    let emergencyCharge = 0;
    let outsideOPDEmergencyCharge = 0;

    // Add service charges
    selectedServices.forEach((service) => {
      grossAmount += parseFloat(service.unitPrice);
      discountAmount +=
        (parseFloat(service.unitPrice) * parseFloat(service.discount)) / 100;
    });

    // Add emergency charge if appointment is emergency
    const doctorEmergencyCharge =
      selectedDoctorDetails?.doctor?.emergency_charge ||
      selectedDoctorDetails?.emergency_charge;
    // WVZXZYV
    const DocteroutsideOPDEmergencyCharge =
      selectedDoctorDetails?.doctor?.outside_opd_charge || 0;

    if (
      appointmentData.isOutsideOPDEmergency === 1 &&
      DocteroutsideOPDEmergencyCharge
    ) {
      // outsideOPD emergency charge logic
      outsideOPDEmergencyCharge = parseFloat(DocteroutsideOPDEmergencyCharge);
      grossAmount += outsideOPDEmergencyCharge;
      console.log(
        "outside OPD Emergency charge added to bill:",
        outsideOPDEmergencyCharge
      );
    }
    if (appointmentData.isEmergency === 1 && doctorEmergencyCharge) {
      emergencyCharge = parseFloat(doctorEmergencyCharge);
      grossAmount += emergencyCharge;
      console.log("Emergency charge added to bill:", emergencyCharge);
    }
    const subtotal = grossAmount - discountAmount;
    // Prefer existing bill's tax percentage if present; otherwise use doctor's tax percentage or default 0%
    const taxPercent = Number(
      selectedDoctorDetails?.doctor?.tax_percentage ??
      selectedDoctorDetails?.doctor?.taxPercentage ??
      selectedDoctorDetails?.tax_percentage ??
      selectedDoctorDetails?.taxPercentage ??
      0
    );
    const taxAmount = subtotal * (taxPercent / 100);
    const netAmount = subtotal + taxAmount;

    setBillSummary({
      grossAmount: grossAmount.toFixed(2),
      subtotal: subtotal.toFixed(2),
      discountAmount: Math.abs(discountAmount).toFixed(2), // ✅ force positive
      emergencyCharge: emergencyCharge.toFixed(2),
      outsideOPDEmergencyChange: outsideOPDEmergencyCharge.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      taxPercentage: Number(taxPercent),
      netAmount: netAmount.toFixed(2),
      // netAmount: 400,
    });

    console.log(JSON.stringify(billSummary));
    setIsBillGenerated(true);
  };

  // Auto-update bill summary when inputs change
  useEffect(() => {
    try {
      handleGenerateBillClick();
    } catch (_) {
      // no-op
    }
    // Recompute bill whenever services, doctor, date, time or emergency flags change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedServices,
    appointmentData.isEmergency,
    appointmentData.OutsideOPDEmergency,
    appointmentData.doctorId,
    appointmentData.appointmentDate,
    appointmentData.appointmentTime,
    selectedDoctorDetails,
  ]);

  // Handle service selection and auto-populate unit price and discount from doctor's service data
  const handleServiceSelection = (serviceName) => {
    const selectedService = doctorServices.find(
      (service) => service.name === serviceName
    );

    if (selectedService) {
      // Auto-populate unit price and discount from the selected service
      setServiceDetails({
        serviceId: selectedService.name,
        unitPrice: selectedService.unitPrice || "",
        discount: selectedService.discount || "",
      });
      console.log("Service selected:", selectedService);
    } else {
      // Clear fields if service not found
      setServiceDetails({
        serviceId: serviceName,
        unitPrice: "",
        discount: "",
      });
    }
  };

  const addService = () => {
    // Validate required fields before adding service
    if (
      serviceDetails.serviceId &&
      serviceDetails.unitPrice &&
      serviceDetails.discount !== ""
    ) {
      const selectedService = doctorServices.find(
        (service) => service.name === serviceDetails.serviceId
      );

      if (selectedService) {
        // Check if service is already added
        const isServiceAlreadyAdded = selectedServices.some(
          (service) => service.serviceName === selectedService.name
        );

        if (isServiceAlreadyAdded) {
          toast({
            title: "Service Already Added",
            description:
              "This service has already been added to the appointment.",
            status: "warning",
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        setSelectedServices([
          ...selectedServices,
          {
            serviceName: selectedService.name,
            unitPrice: serviceDetails.unitPrice,
            discount: serviceDetails.discount,
          },
        ]);

        toast({
          title: "Service Added",
          description: `${selectedService.name} has been added to the appointment.`,
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      }

      // Clear the form after adding
      setServiceDetails({ serviceId: "", unitPrice: "", discount: "" });
    } else {
      toast({
        title: "Missing Information",
        description:
          "Please select a service and enter unit price and discount.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const removeService = (index) => {
    const updatedServices = selectedServices.filter((_, i) => i !== index);
    setSelectedServices(updatedServices);
  };

  // send via whatsapp / email / print helpers removed (not used)

  const handleAddAppointment = async () => {
    // alert(JSON.parse()));
    // alert(appointmentData.appointmentTime);
    console.log("Add Appointment endpoint:", API_ENDPOINTS.APPOINTMENT.ADD);
    console.log("=== APPOINTMENT BOOKING - PATIENT ID CHECK ===");
    console.log("appointmentData.patientId:", appointmentData.patientId);
    console.log(
      "appointmentData.patientId type:",
      typeof appointmentData.patientId
    );
    console.log(
      "parseInt(appointmentData.patientId):",
      parseInt(appointmentData.patientId)
    );
    console.log("=== END PATIENT ID CHECK ===");

    // Validate appointment date is not in the past
    if (isDateInPast(appointmentData.appointmentDate)) {
      toast({
        title: "Invalid Date",
        description:
          "Cannot book appointment for past dates. Please select a valid date.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate appointment time is not in the past for today's appointments
    if (
      appointmentData.appointmentTime &&
      isTimeSlotInPast(
        appointmentData.appointmentTime,
        appointmentData.appointmentDate
      ) &&
      !appointmentData.isOutsideOPDEmergency
    ) {
      const currentTimeExact = getCurrentTimeExact();
      const isToday = appointmentData.appointmentDate === minSelectableDate;
      const timeMessage = isToday
        ? `Cannot book appointment for past time slots. Current time is ${currentTimeExact}. Please select a time slot at least 30 minutes from now.`
        : "Cannot book appointment for past time slots. Please select a future time.";

      toast({
        title: "Invalid Time Selection",
        description: timeMessage,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      // Prepare services array with emergency charge if applicable

      // Add emergency charge as a service if appointment is emergency
      const doctorEmergencyCharge =
        selectedDoctorDetails?.doctor?.emergency_charge ||
        selectedDoctorDetails?.emergency_charge;
      if (
        appointmentData.isEmergency === 1 &&
        doctorEmergencyCharge &&
        appointmentData.isOutsideOPDEmergency === 0
      ) {
        const emergencyCharge = parseFloat(doctorEmergencyCharge);
        selectedServices.push({
          serviceName: "Emergency Charge",
          unitPrice: emergencyCharge,
          discount: 0,
        });
        console.log(
          "Emergency charge added to appointment services:",
          emergencyCharge
        );
      }
      const doctor_outside_opd_charge =
        selectedDoctorDetails?.doctor?.outside_opd_charge || 0;
      if (
        appointmentData.isOutsideOPDEmergency === 1 &&
        doctorEmergencyCharge &&
        appointmentData.isEmergency === 0
      ) {
        const outside_opd_charge = doctor_outside_opd_charge;
        // alert(JSON.stringify(selectedDoctorDetails?.doctor));
        // alert(outside_opd_charge);
        selectedServices.push({
          serviceName: "OutSide OPD Charge",
          unitPrice: outside_opd_charge,
          discount: 0,
        });
        console.log(
          "Emergency charge added to appointment services:",
          outside_opd_charge
        );
      }

      const appointment_time = appointmentData.isEmergency
        ? `${String(new Date().getHours()).padStart(2, "0")}:${String(
          new Date().getMinutes()
        ).padStart(2, "0")}`
        : appointmentData.appointmentTime;

      // const appointment_time = appointmentData.appointmentTime.split(" ")[0];
      // Format the appointment data according to the API requirements
      const appointmentPayload = {
        patient_id: parseInt(appointmentData.patientId),
        doctor_id: parseInt(appointmentData.doctorId),
        appointment_date: appointmentData.appointmentDate,
        appointment_time: appointment_time, // Extract time without AM/PM
        appointment_type: appointmentData.appointmentType,
        services: selectedServices,
        isEmergency: appointmentData.isEmergency,
        isOutsideOPDEmergency: appointmentData.isOutsideOPDEmergency,
        referred_by: null,
        frontdesk_id: frontdesk_id,
      };

      // Store in localStorage before making API call
      const storedAppointments = JSON.parse(
        localStorage.getItem("appointments") || "[]"
      );
      const newAppointment = {
        id: "temp_" + Date.now(), // Temporary ID until API response
        ...appointmentPayload,
        created_date: new Date().toISOString(),
        status: "BOOKED",
        raw_status: "BOOKED",
        name: appointmentData.patientName,
        doctor_name:
          doctors.find((d) => d.id === parseInt(appointmentData.doctorId))
            ?.doctor || "Unknown Doctor",
        phoneNumber: appointmentData.phone_no,
        treatment_type: appointmentData.appointmentType,
        booking_time: appointmentData.appointmentTime,
        payment_action: "PENDING",
      };

      storedAppointments.push(newAppointment);
      localStorage.setItem("appointments", JSON.stringify(storedAppointments));
      console.log("Stored appointments in localStorage:", storedAppointments);

      console.log("Sending appointment data:", appointmentPayload);
      setAppointmentPayload(appointmentPayload);

      // Make the API request using axiosInstance
      const response = await axiosInstance.post(
        API_ENDPOINTS.APPOINTMENT.ADD,
        appointmentPayload,
        { headers: getAuthHeaders() }
      );

      console.log("Appointment created successfully:", response.data);

      // Update the temporary ID with the real one from API response
      const updatedAppointments = JSON.parse(
        localStorage.getItem("appointments") || "[]"
      );
      const updatedAppointment = updatedAppointments.find(
        (apt) => apt.id === newAppointment.id
      );
      if (updatedAppointment) {
        updatedAppointment.id =
          response.data.id || response.data.appointment_id;
        localStorage.setItem(
          "appointments",
          JSON.stringify(updatedAppointments)
        );
        console.log(
          "Updated appointment ID in localStorage:",
          updatedAppointments
        );
      }

      // Show success toast
      toast({
        title: "Appointment Booked",
        description: "The appointment has been successfully booked.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Call the success callback with the new appointment data
      if (onAddAppointmentSuccess) {
        onAddAppointmentSuccess(response.data);
      }

      //   Get appointment id from response (support multiple response shapes)
      const apptID = response.data?.id || response.data?.appointment_id;
      console.log(
        "[Billing] Starting bill creation flow for appointment response:",
        response.data
      );
      setAppointmentID(apptID);

      // Keep the booking modal open and show bill summary side-by-side
      setBillSummaryVisible(true);
      // default selection not made yet
      setBillPaymentNowSelection(null);
      // initialize simplified payment state defaults
      setPaymentState(PaymentStates.PENDING);
      // setPaidAmount(0);
      // setAmountReceivedInput(String(parseFloat(billSummary?.netAmount || 0)));
      // open bill view is separate; user can view the bill or continue to payment
    } catch (error) {
      console.error("Error creating appointment:", error);
      toast({
        title: "Error",
        description:
          error.response?.data?.message ||
          "Failed to book appointment. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const MAX_FILE_BYTES = 2 * 1024 * 1024; // 2MB

  // Dynamically load jsPDF from CDN if not available in bundle
  const loadJsPDF = () =>
    new Promise((resolve, reject) => {
      if (window.jspdf?.jsPDF) return resolve(window.jspdf.jsPDF);
      const script = document.createElement("script");
      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
      script.async = true;
      script.onload = () => {
        if (window.jspdf?.jsPDF) resolve(window.jspdf.jsPDF);
        else reject(new Error("jsPDF failed to initialize"));
      };
      script.onerror = () => reject(new Error("Failed to load jsPDF from CDN"));
      document.head.appendChild(script);
    });

  const readFileAsDataURL = (file) =>
    new Promise((resolve, reject) => {
      try {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      } catch (e) {
        reject(e);
      }
    });

  const loadImageEl = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.crossOrigin = "anonymous";
      img.src = src;
    });

  // Compress large images (>2MB) and return a dataURL (JPEG)
  const compressImageToDataURL = async (file, targetBytes = MAX_FILE_BYTES) => {
    const dataUrl = await readFileAsDataURL(file);
    const img = await loadImageEl(dataUrl);

    // Scale down if too large (cap long side to 2000px)
    const maxSide = Math.max(img.width, img.height);
    const cap = 2000;
    const scale = maxSide > cap ? cap / maxSide : 1;

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    let quality = 0.9;
    let outBlob = await new Promise((res) =>
      canvas.toBlob(res, "image/jpeg", quality)
    );

    // Iteratively reduce quality until under targetBytes or min quality reached
    while (outBlob && outBlob.size > targetBytes && quality > 0.4) {
      quality -= 0.1;
      outBlob = await new Promise((res) =>
        canvas.toBlob(res, "image/jpeg", quality)
      );
    }

    // If canvas.toBlob returned null, fallback to original
    if (!outBlob) return dataUrl;
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.readAsDataURL(outBlob);
    });
  };

  // Convert any image dataURL to a single-page PDF Blob
  const imageDataURLToPdfBlob = async (imageDataURL) => {
    const jsPDF = await loadJsPDF();
    const img = await loadImageEl(imageDataURL);

    // Use pt to avoid rounding errors; fit image proportionally to A4
    const pdf = new jsPDF({
      orientation: img.width > img.height ? "l" : "p",
      unit: "pt",
      format: "a4",
    });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();

    // const r = Math.min(pageW / img.width, pageH / img.height);
    // const drawW = img.width * r;
    // // unit: "pt";
    // // format: "a4";
    // });
    // const pageW = pdf.internal.pageSize.getWidth();
    // const pageH = pdf.internal.pageSize.getHeight();

    const r = Math.min(pageW / img.width, pageH / img.height);
    const drawW = img.width * r;
    const drawH = img.height * r;
    const x = (pageW - drawW) / 2;
    const y = (pageH - drawH) / 2;

    // Infer format type from dataURL header
    const fmt = imageDataURL.includes("image/png") ? "PNG" : "JPEG";
    pdf.addImage(imageDataURL, fmt, x, y, drawW, drawH);
    return pdf.output("blob");
  };

  // Unified robust uploader with retry, timeout and fallback
  const smartUpload = async (blobOrFile, { fileName, mimeType } = {}) => {
    const uploadTimeoutMs = 25000;
    const attempts = 3;

    const tryUploadFile = async () => {
      if (typeof uploadFile !== "function")
        throw new Error("uploadFile missing");
      return await Promise.race([
        uploadFile(blobOrFile, { fileName, mimeType }),
        timeoutPromise(uploadTimeoutMs, "Upload timed out"),
      ]);
    };

    // Fallback single-file endpoint
    const tryFallbackRest = async () => {
      const formData = new FormData();
      const file =
        blobOrFile instanceof File
          ? blobOrFile
          : new File([blobOrFile], fileName || `upload_${Date.now()}`, {
            type: mimeType || "application/octet-stream",
          });
      formData.append("file", file);
      const resp = await axiosInstance.post("/api/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        maxBodyLength: Infinity,
        timeout: uploadTimeoutMs,
      });
      const url =
        resp?.data?.objectUrl || resp?.data?.url || resp?.data?.fileUrl || null;
      if (!url) throw new Error("Fallback upload did not return URL");
      return url;
    };

    let lastErr;
    for (let i = 0; i < attempts; i++) {
      try {
        return await tryUploadFile();
      } catch (e) {
        lastErr = e;
        await sleep(500 * Math.pow(2, i));
      }
    }
    // Fallback once
    return await tryFallbackRest().catch((e) => {
      throw lastErr || e;
    });
  };

  // Normalize, convert and upload a list of receipts
  const prepareAndUploadReceipts = async (input) => {
    // Normalize input to array<File>
    const toArray = (v) =>
      !v
        ? []
        : v instanceof File
          ? [v]
          : v instanceof FileList
            ? Array.from(v)
            : Array.isArray(v)
              ? v
              : [v];

    const files = toArray(input);
    if (!files.length) return [];

    const uploaders = files.map(async (f, idx) => {
      try {
        let fileNameBase =
          f.name?.replace(/\.[^.\s]+$/, "") || "payment_receipt";
        const ts = Date.now();

        if (f.type?.startsWith("image/")) {
          // Compress if > 2MB, then convert every image to PDF
          const dataUrl =
            f.size > MAX_FILE_BYTES
              ? await compressImageToDataURL(f, MAX_FILE_BYTES)
              : await readFileAsDataURL(f);
          const pdfBlob = await imageDataURLToPdfBlob(dataUrl);
          const pdfName = `${fileNameBase}_${ts}.pdf`;
          return await smartUpload(pdfBlob, {
            fileName: pdfName,
            mimeType: "application/pdf",
          });
        }

        // Non-image: upload as-is; PDFs may be already optimal
        const extMatch = (f.name || "").match(/\.[^.\s]+$/);
        const inferredExt = extMatch?.[0] || "";
        const safeName = `${fileNameBase}_${ts}${inferredExt}`;
        return await smartUpload(f, {
          fileName: safeName,
          mimeType: f.type || "application/octet-stream",
        });
      } catch (e) {
        console.warn("Receipt upload failed for a file:", e?.message || e);
        return null;
      }
    });

    const results = await Promise.all(uploaders);
    return results.filter(Boolean);
  };

  const uploadAndSyncBillFiles = async ({
    createdBillId,
    invoiceBlob,
    receiptsInput,
  }) => {
    if (!createdBillId) throw new Error("Missing bill id for upload sync.");

    // Upload invoice and receipts concurrently
    const invoicePromise = smartUpload(invoiceBlob, {
      fileName: `bill_invoice_${Date.now()}.pdf`,
      mimeType: "application/pdf",
    });

    // receiptsInput may be an array of File objects, or array of items {file,url,name}
    const existingUrls = Array.isArray(receiptsInput)
      ? receiptsInput.filter((it) => it && it.url).map((it) => it.url)
      : [];

    // Files that need uploading
    const filesToUpload = Array.isArray(receiptsInput)
      ? receiptsInput.filter((it) => it && it.file).map((it) => it.file)
      : Array.isArray(receiptsInput)
        ? receiptsInput
        : [];

    const receiptsPromise = prepareAndUploadReceipts(filesToUpload);

    const [invoiceUrl, newlyUploaded] = await Promise.all([
      invoicePromise,
      receiptsPromise,
    ]);

    const receiptUrls = [...(existingUrls || []), ...(newlyUploaded || [])];

    if (!invoiceUrl && (!receiptUrls || receiptUrls.length === 0)) {
      throw new Error("Nothing to upload for bill sync");
    }

    const syncResponse = await uploadReceiptsandBill({
      billId: createdBillId,
      invoiceUrl,
      receiptUrls,
    });

    return { invoiceUrl, receiptUrls, syncResponse };
  };

  const updatePaymentStatus = async ({
    appointmentId,
    paymentAction,
    paidAmount,
    netAmount,
  }) => {
    if (!appointmentId) return;
    try {
      await axiosInstance.post("/api/appointment/updatePaymentStatus", {
        appointment_id: appointmentId,
        payment_action: paymentAction,
        paid_amount: paidAmount,
        pending_amount: Math.max(0, (netAmount || 0) - (paidAmount || 0)),
      });
    } catch (e) {
      console.warn("updatePaymentStatus failed:", e?.message || e);
    }
  };

  const net = parseFloat(billSummary?.netAmount || 0);
  const rec = parseFloat(billSummary.RecivedAmt || "0");

  const CreateBill = async () => {
    // Validate common booking constraints (fast-fail)
    if (isNaN(rec) || rec < 0) {
      toast({
        title: "Invalid amount",
        description: "Please enter a valid amount received (>= 0)",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (isDateInPast(appointmentData.appointmentDate)) {
      toast({
        title: "Invalid Date",
        description:
          "Cannot book appointment for past dates. Please select a valid date.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (
      appointmentData.appointmentTime &&
      isTimeSlotInPast(
        appointmentData.appointmentTime,
        appointmentData.appointmentDate
      ) &&
      !appointmentData.isOutsideOPDEmergency
    ) {
      const currentTimeExact = getCurrentTimeExact();
      const isToday = appointmentData.appointmentDate === minSelectableDate;
      toast({
        title: "Invalid Time Selection",
        description: isToday
          ? `Cannot book appointment for past time slots. Current time is ${currentTimeExact}. Please select a time slot at least 10 minutes from now.`
          : "Cannot book appointment for past time slots. Please select a future time.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    // 2) Create bill on server (retry-safe)
    const taxPercentageForPayload =
      billSummary.taxPercentage ??
      billSummary.tax_percent ??
      selectedDoctorDetails?.doctor?.tax_percentage ??
      selectedDoctorDetails?.doctor?.taxPercentage ??
      selectedDoctorDetails?.tax_percentage ??
      selectedDoctorDetails?.taxPercentage ??
      10;

    const billPayload = {
      appointment_id: appointmentID,
      consultation_type:
        appointmentPayload?.appointment_type || appointmentData.appointmentType,
      total_amt: String(net || 0),
      tax_amount: Number(billSummary.taxAmount || 0),
      tax_percentage: Number(taxPercentageForPayload),
      received_amt: rec,
      pending_amt: parseFloat(billSummary.PendingAmt || "0"),
      services: selectedServices || [],
      bill_description: bill_desp || "",
    };
    const billingUrl = `${billsBaseUrl}/create`;
    const authToken =
      localStorage.getItem("token") || localStorage.getItem("Token") || "";
    const headers = {
      "Content-Type": "application/json",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    };

    const billResponse = await postJsonWithRetry(
      billingUrl,
      billPayload,
      { headers, timeout: 20000 },
      3
    );
    const createdBillId =
      billResponse?.data?.billId ||
      billResponse?.data?.id ||
      billResponse?.data?.bill_id ||
      null;

    if (!createdBillId) throw new Error("Bill creation failed");

    setCreatedBillId(createdBillId);
    setCreatedAppointmentId(appointmentID);
  };

  const handleSaveBill = async () => {
    try {
      // 1) Generate invoice PDF blob (quick)
      const invoiceBlob = await Promise.race([
        generateBillPdf(
          "bill-preview",
          false,
          `bill_${appointmentData.patientName || "patient"}.pdf`
        ),
        timeoutPromise(20000, "Generating PDF timed out"),
      ]);
      if (!invoiceBlob) throw new Error("Failed to generate PDF");

      // 3) Upload only the invoice here. Receipts were uploaded when added via the UI
      // receiptItems state contains {name, url} entries (no File objects) after uploadFilesAndSetUrls
      const invoiceUrl = await smartUpload(invoiceBlob, {
        fileName: `bill_invoice_${Date.now()}.pdf`,
        mimeType: "application/pdf",
      });

      // Collect existing receipt URLs from receiptItems and dedupe
      const existingReceiptUrls = Array.isArray(receiptItems)
        ? receiptItems.map((it) => it && it.url).filter(Boolean)
        : [];
      const uniqueReceiptUrls = Array.from(new Set(existingReceiptUrls));

      // Sync invoice URL and existing receipt URLs with server
      const syncResponse = await uploadReceiptsandBill({
        billId: createdBillId,
        invoiceUrl,
        receiptUrls: uniqueReceiptUrls,
      }).catch((e) => {
        console.warn("uploadReceiptsandBill failed:", e?.message || e);
        return null;
      });

      // setinvoiceUrl(invoiceUrl);

      const syncMsg =
        (syncResponse && syncResponse?.data?.message) ||
        "Bill invoice and receipts uploaded successfully.";
      toast({
        title: "Files synced",
        description: syncMsg,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Persist bill metadata (services/amounts/description) using resilient updater
      // try {
      //     if (createdBillId) {
      //     // Compute a consistent tax percentage for payload here (same logic as in CreateBill)
      //     const taxPercentageForPayload =
      //       billSummary.taxPercentage ??
      //       billSummary.tax_percent ??
      //       selectedDoctorDetails?.doctor?.tax_percentage ??
      //       selectedDoctorDetails?.doctor?.taxPercentage ??
      //       selectedDoctorDetails?.tax_percentage ??
      //       selectedDoctorDetails?.taxPercentage ??
      //       10;

      //     const updatePayload = {
      //       appointment_id: createdAppointmentId || appointmentID,
      //       consultation_type:
      //         appointmentPayload?.appointment_type ||
      //         appointmentData.appointmentType,
      //       total_amt: String(net || 0),
      //       tax_amount: Number(billSummary.taxAmount || 0),
      //       tax_percentage: Number(taxPercentageForPayload),
      //       received_amt: rec,
      //       pending_amt: parseFloat(billSummary.PendingAmt || "0"),
      //       services: selectedServices || [],
      //       bill_description: bill_desp || "",
      //       // Attach file links if available
      //       bill_invoice_url: invoiceUrl,
      //       payment_receipt_url: uniqueReceiptUrls,
      //     };
      //     await saveOrUpdateBill(createdBillId, updatePayload);
      //   }
      // } catch (e) {
      //   console.warn("Failed to save bill metadata after file upload", e);
      // }

      // 4) Update payment status (best-effort)
      const appointmentId =
        createdAppointmentId ||
        (selectedPaymentRow && selectedPaymentRow.id) ||
        (() => {
          const stored = JSON.parse(
            localStorage.getItem("appointments") || "[]"
          );
          return stored.length ? stored[stored.length - 1].id : null;
        })();

      let desiredAction = "PENDING";
      if (net === rec) {
        desiredAction = "PAID";
      } else if (rec > 0 && rec < net) {
        desiredAction = "PENDING";
      }
      await updatePaymentStatus({
        appointmentId,
        paymentAction: desiredAction,
        paidAmount: rec,
        netAmount: net,
      });

      // 5) Finalize
      toast({
        title: "Bill saved",
        description: "Appointment bill saved successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (billErr) {
      console.error("[Billing] Error creating bill:", billErr);
      toast({
        title: "Billing Error",
        description:
          "Failed to create bill. " +
          (billErr?.response?.data?.message || billErr.message || "Try again."),
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    }
  };

  const appointmentTypes = [
    { value: "in_clinic", label: "In Clinic" },
    { value: "online", label: "Online" },
  ];

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        isCentered
        motionPreset="slideInBottom"
        size="lg" // Adjusted for larger modal to accommodate A4 bill
        w="100vw"
        h="100vh"
      >
        <ModalOverlay />
        <ModalContent
          p={6}
          mt={16}
          borderTop={
            appointmentData.isEmergency || appointmentData.isOutsideOPDEmergency
              ? "4px solid red"
              : undefined
          }
          minW={"550px"}
          maxH="100vh"
          maxW="45vw" // Ensure modal fits screen
        >
          <ModalHeader mb={2}>
            <Flex alignItems="center" gap={2}>
              {(appointmentData.isEmergency === 1 ||
                appointmentData.isOutsideOPDEmergency === 1) && (
                  <AmbulanceIcon size={20} color="red" />
                )}
              <Text fontSize="lg">
                {appointmentData.isEmergency
                  ? "Book Emergency Appointment"
                  : appointmentData.isOutsideOPDEmergency
                    ? "Book Outside OPD Emergency Appointment"
                    : "Book Appointment"}
              </Text>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody maxH="90vh" overflowY="auto">
            <Flex direction="cols" gap={8}>
              {/* Left: Appointment Form */}
              <Box flex="1" minW="0">
                <Flex alignItems="center" justifyContent="center" mb={4}>
                  <Avatar size="xl" src={""} mr="20px" />
                  <Box>
                    <Text fontWeight="bold" fontSize="2xl" mb="1">
                      {appointmentData.patientName}
                      <Text
                        as="span"
                        fontWeight="normal"
                        fontSize="md"
                        color="gray.500"
                        ml="2"
                      >
                        ({appointmentData.patientId})
                      </Text>
                    </Text>
                    <Text color="gray.600" mb={1}>
                      {appointmentData.patientGender},{" "}
                      {appointmentData.patientAge} years old
                    </Text>
                    <Text color="gray.600" mb={2}>
                      <b>Phone No:</b> {appointmentData.phone_no}
                    </Text>
                    <Flex flexDirection="row" mt="20px"></Flex>
                  </Box>
                </Flex>
                <Box height="24px" />
                <Flex mb={6} direction="row" justifyContent="space-between">
                  <Flex direction="column" width="30%">
                    <FormLabel htmlFor="doctorSelect">Doctor</FormLabel>

                    {isLoggedInDoctor ? (
                      <>
                        <Text
                          fontSize="md"
                          color="black"
                        // fontWeight={"bold"}
                        >
                          {currentDoctorName}
                        </Text>
                        <Text fontSize="sm" color="blue.500">
                          Automatically selected as current doctor
                        </Text>
                      </>
                    ) : (
                      <Select
                        id="doctorSelect"
                        name="doctorId"
                        placeholder={currentDoctor.doctor || "Select Doctor"}
                        onChange={handleInputChange}
                        mb={2}
                        value={currentDoctor}
                        isDisabled={isLoggedInDoctor}
                      >
                        {doctors.map((doctor) => (
                          <option key={doctor.id} value={doctor.id}>
                            {doctor.doctor}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Flex>
                </Flex>
                {/* Add Emergency Checkbox */}
                {outsideOPDEmergency === 0 &&
                  appointmentData.isOutsideOPDEmergency === 0 && (
                    <Flex mb={6}>
                      <FormControl display="flex" alignItems="center">
                        <FormLabel
                          htmlFor="emergency"
                          mb="0"
                          mr={4}
                          color="red.500"
                        >
                          Emergency Appointment
                        </FormLabel>
                        <Switch
                          id="emergency"
                          isChecked={appointmentData.isEmergency === 1}
                          onChange={(e) => {
                            setAppointmentData((prev) => ({
                              ...prev,
                              isEmergency: e.target.checked ? 1 : 0,
                            }));
                          }}
                          colorScheme="red"
                        />
                      </FormControl>
                    </Flex>
                  )}

                {/* OutSide OPD Toogle */}
                {outsideOPDEmergency === 1 &&
                  appointmentData.isOutsideOPDEmergency === 1 &&
                  appointmentData.isEmergency === 0 && (
                    <Flex mb={6}>
                      <FormControl display="flex" alignItems="center">
                        <FormLabel
                          color="red.500"
                          mr={4}
                          htmlFor="outsideopdemergency"
                          mb="0"
                        >
                          Outside OPD Emergency
                        </FormLabel>
                        <Switch
                          id="emergency"
                          isChecked={outsideOPDEmergency === 1}
                          colorScheme="red"
                        />
                      </FormControl>
                    </Flex>
                  )}

                <Flex
                  mb={6}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Flex direction="column" width="30%">
                    <FormLabel htmlFor="appointmentDate">
                      Appointment Date
                    </FormLabel>
                    <Input
                      type="date"
                      id="appointmentDate"
                      name="appointmentDate"
                      placeholder="Appointment Date"
                      value={appointmentData.appointmentDate}
                      onChange={handleInputChange}
                      mb={2}
                      min={minSelectableDate}
                      isDisabled={!appointmentData.doctorId || appointmentData.isEmergency === 1}
                      isInvalid={isDateInPast(appointmentData.appointmentDate)}
                      sx={{
                        "&::-webkit-calendar-picker-indicator": {
                          cursor: "pointer",
                        },
                        _invalid: {
                          borderColor: "red.300",
                          boxShadow: "0 0 0 1px red.300",
                        },
                        ...(isDateInPast(appointmentData.appointmentDate) && {
                          borderColor: "red.300",
                          boxShadow: "0 0 0 1px red.300",
                          backgroundColor: "red.50",
                        }),
                      }}
                    />
                    <Text
                      fontSize="sm"
                      color={
                        isDateInPast(appointmentData.appointmentDate)
                          ? "red.500"
                          : "gray.500"
                      }
                      mt={1}
                    ></Text>
                  </Flex>

                  <Flex direction="column" width="30%">
                    <FormLabel htmlFor="appointmentTime" colorScheme="red">
                      Appointment Time
                    </FormLabel>
                    <Select
                      id="appointmentTime"
                      name="appointmentTime"
                      placeholder="Select Time"
                      onChange={handleInputChange}
                      mb={2}
                      value={appointmentData.appointmentTime}
                      isDisabled={
                        !appointmentData.appointmentDate
                        ||
                        !appointmentData.doctorId || appointmentData.isEmergency === 1
                      }
                      sx={{
                        "& option": {
                          color: "inherit",
                        },
                      }}
                    >
                      {ALL_TIME_SLOTS.map((ts, index) => {
                        const found = availableTimeSlots.find(
                          (s) => s.value === ts
                        );
                        const isPast =
                          isTimeSlotInPast(
                            ts,
                            appointmentData.appointmentDate
                          ) ||
                          (found && found.isPast);
                        const isAvailable = !!found && !isPast;
                        return (
                          <option
                            key={ts}
                            value={ts}
                            disabled={isPast}
                            style={{
                              color: isPast
                                ? "#A0A0A0"
                                : isAvailable
                                  ? "inherit"
                                  : "#B00020", // red for outside OPD
                              backgroundColor: isPast ? "#F5F5F5" : "inherit",
                              fontStyle: isPast
                                ? "italic"
                                : isAvailable
                                  ? "normal"
                                  : "italic",
                            }}
                          >
                            {formatTime12h(ts)}
                            {!isAvailable && !isPast ? " (Outside OPD)" : ""}
                            {isPast ? " (Past)" : ""}
                          </option>
                        );
                      })}
                    </Select>
                    {(!appointmentData.appointmentDate ||
                      !appointmentData.doctorId) && (
                        <Text fontSize="sm" color="gray.500">
                          Please select both date and doctor first
                        </Text>
                      )}
                    {appointmentData.appointmentDate &&
                      appointmentData.doctorId &&
                      appointmentData.isEmergency === 0 &&
                      availableTimeSlots.length === 0 && (
                        <Text fontSize="sm" color="red.500">
                          No OPD available slots for this date
                        </Text>
                      )}
                  </Flex>

                  <Flex direction="column" width="30%">
                    <FormLabel htmlFor="appointmentType">
                      Appointment Type
                    </FormLabel>
                    <Select
                      id="appointmentType"
                      name="appointmentType"
                      placeholder="Select Appointment Type"
                      value={appointmentData.appointmentType}
                      onChange={handleInputChange}
                      mb={2}
                      isRequired
                    >
                      {appointmentTypes.map((type, index) => (
                        <option key={index} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </Select>
                  </Flex>
                </Flex>

                {/* Service Selection Section */}
                <Flex
                  mb={6}
                  direction="row"
                  gap={2}
                  justifyContent="space-between"
                >
                  <Flex direction="column" width="30%">
                    <FormLabel htmlFor="serviceSelect">Service</FormLabel>
                    <Select
                      id="serviceSelect"
                      placeholder={
                        isLoadingServices
                          ? "Loading services..."
                          : "Select Service"
                      }
                      onChange={(e) => handleServiceSelection(e.target.value)}
                      mb={2}
                      value={serviceDetails.serviceId}
                      isRequired
                      isDisabled={
                        !appointmentData.doctorId ||
                        doctorServices.length === 0 ||
                        isLoadingServices
                      }
                    >
                      {doctorServices &&
                        doctorServices.map((service) => (
                          <option key={service.id} value={service.name}>
                            {service.name}
                          </option>
                        ))}
                    </Select>
                    {!appointmentData.doctorId && (
                      <Text fontSize="sm" color="gray.500">
                        Please select a doctor first
                      </Text>
                    )}
                    {isLoadingServices && (
                      <Text fontSize="sm" color="blue.500">
                        Loading doctor services...
                      </Text>
                    )}
                    {appointmentData.doctorId &&
                      !isLoadingServices &&
                      doctorServices.length === 0 && (
                        <Text fontSize="sm" color="orange.500">
                          No services available for this doctor
                        </Text>
                      )}
                  </Flex>

                  <Flex direction="column" width="30%">
                    <FormLabel htmlFor="unitPrice">Unit Price</FormLabel>
                    <Input
                      id="unitPrice"
                      placeholder="Unit Price"
                      value={serviceDetails.unitPrice}
                      onChange={(e) =>
                        setServiceDetails({
                          ...serviceDetails,
                          unitPrice: e.target.value,
                        })
                      }
                      mb={2}
                      type="number"
                      min="0"
                    />
                  </Flex>

                  <Flex direction="column" width="30%">
                    <FormLabel htmlFor="discount">Discount (%)</FormLabel>
                    <Input
                      id="discount"
                      placeholder="Discount %"
                      value={serviceDetails.discount}
                      onChange={(e) =>
                        setServiceDetails({
                          ...serviceDetails,
                          discount: e.target.value,
                        })
                      }
                      mb={2}
                      type="number"
                      min="0"
                      max="100"
                    />
                  </Flex>

                  <Flex alignItems="flex-end" mb={2} ml={4}>
                    <Button
                      onClick={addService}
                      colorScheme="blue"
                      height="45px"
                    >
                      Add
                    </Button>
                  </Flex>
                </Flex>

                {selectedServices.length > 0 && (
                  <Flex direction="column" mb={6}>
                    <FormLabel>Selected Services</FormLabel>
                    {selectedServices.map((service, index) => (
                      <Flex
                        key={index}
                        justifyContent="space-between"
                        alignItems="center"
                        mb={3}
                      >
                        <p>{service.serviceName || "Unknown Service"}</p>
                        <p>Unit Price: ₹{service.unitPrice}</p>
                        <p>Discount: {service.discount}%</p>
                        <Button
                          colorScheme="red"
                          onClick={() => removeService(index)}
                        >
                          Remove
                        </Button>
                      </Flex>
                    ))}
                  </Flex>
                )}
              </Box>
              {/* /* Right: Bill Summary (shown after booking) */}
              {/* Bill Summary now uses a proper Chakra Modal for a cleaner, professional UI */}
              <Modal
                isOpen={!!billSummaryVisible}
                // isOpen={true}
                // onClose={() => setBillSummaryVisible(false)}
                isCentered
                size="md"
              >
                <ModalOverlay />
                <ModalContent p={2} borderRadius="lg" boxShadow="lg">
                  <ModalHeader>
                    <Flex align="center" justify="space-between">
                      <Text fontSize="lg" fontWeight="600">
                        Bill Summary
                      </Text>
                      {/* <IconButton
                        aria-label="Close summary"
                        icon={<CloseIcon />}
                        size="sm"
                        variant="ghost"
                        onClick={() => setBillSummaryVisible(false)}
                      />*/}
                    </Flex>
                  </ModalHeader>
                  <ModalBody>
                    <VStack spacing={4} align="stretch">
                      <Box>
                        <Flex justify="space-between" fontWeight="600" mb={2}>
                          <Text>Service</Text>
                          <Text>Discount</Text>
                          <Text textAlign="right">Amount</Text>
                        </Flex>
                        <Stack spacing={2}>
                          {selectedServices && selectedServices.length > 0 ? (
                            selectedServices.map((srv, i) => {
                              const price = parseFloat(srv.unitPrice || 0) || 0;
                              const discount =
                                parseFloat(srv.discount || 0) || 0;
                              const discountAmt = (price * discount) / 100;
                              const lineTotal = price - discountAmt;
                              return (
                                <Flex
                                  key={i}
                                  justify="space-between"
                                  align="center"
                                >
                                  <Text fontSize="sm" color="gray.700">
                                    {srv.serviceName}
                                  </Text>
                                  <Text fontSize="sm" color="gray.600">
                                    {discount}%
                                  </Text>
                                  <Text fontSize="sm" color="gray.800">
                                    ₹{lineTotal.toFixed(2)}
                                  </Text>
                                </Flex>
                              );
                            })
                          ) : (
                            <Text fontSize="sm" color="gray.500">
                              No services selected
                            </Text>
                          )}
                        </Stack>
                      </Box>

                      <Box>
                        {parseFloat(billSummary.emergencyCharge || 0) > 0 && (
                          <Flex justify="space-between">
                            <Text color="red.600">Emergency Charge</Text>
                            <Text color="red.600">
                              ₹{billSummary.emergencyCharge}
                            </Text>
                          </Flex>
                        )}
                        {appointmentData.isOutsideOPDEmergency ? (
                          <Flex justify="space-between">
                            <Text color="red.600">Outside OPD Emergency</Text>
                            <Text color="red.600">
                              {parseFloat(
                                billSummary.outsideOPDEmergencyChange || 0
                              ) > 0
                                ? `₹${billSummary.outsideOPDEmergencyChange}`
                                : "-"}
                            </Text>
                          </Flex>
                        ) : null}

                        <Divider my={3} />

                        <Flex justify="space-between">
                          <Text color="gray.700">Gross</Text>
                          <Text>₹{billSummary.grossAmount}</Text>
                        </Flex>
                        <Flex justify="space-between">
                          <Text color="gray.700">Discount</Text>
                          <Text>-₹{billSummary.discountAmount}</Text>
                        </Flex>
                        <Flex justify="space-between">
                          <Text color="gray.700">
                            Tax (
                            {billSummary.taxPercentage ??
                              selectedDoctorDetails?.doctor?.tax_percentage ??
                              selectedDoctorDetails?.doctor?.taxPercentage ??
                              selectedDoctorDetails?.tax_percentage ??
                              selectedDoctorDetails?.taxPercentage ??
                              0}
                            %)
                          </Text>
                          <Text>₹{billSummary.taxAmount}</Text>
                        </Flex>
                        <Flex justify="space-between" mt={3} fontWeight="700">
                          <Text>Net Amount</Text>
                          <Text>₹{billSummary.netAmount}</Text>
                        </Flex>
                      </Box>

                      <Box>
                        <Text fontSize="sm" color="gray.600" mb={2}>
                          Have you received the net amount?
                        </Text>
                        <Flex gap={3}>
                          <Button
                            variant="outline"
                            textColor={billPaymentNowSelection === "yes" ? "black" : "gray.600"}
                            onClick={() => {

                              setBillPaymentNowSelection("yes");
                              setPaymentState(PaymentStates.PAID);
                              setBillSummary((prev) => ({
                                ...prev,
                                RecivedAmt: prev.netAmount,
                                PendingAmt: 0,
                              }));
                              setispaid(true);
                              setispending(false);
                              setIsReceiptModalOpen(true);
                            }}
                          >
                            Yes
                          </Button>
                          <Button
                            variant="outline"
                            textColor={billPaymentNowSelection === "no" ? "black" : "gray.600"}
                            onClick={() => {
                              setBillPaymentNowSelection("no");
                              setIsReceiptModalOpen(true);
                              setBillSummary((prev) => ({
                                ...prev,
                                RecivedAmt: 0,
                                PendingAmt: prev.netAmount,
                              }));
                              setispaid(false);
                              setispending(true);
                              setPaymentState(PaymentStates.PENDING);
                            }}
                          >
                            No
                          </Button>
                        </Flex>
                      </Box>

                      {isReceiptModalOpen && (
                        <Box>
                          <FormControl>
                            <FormLabel fontWeight="600" fontSize="14px">
                              Amount Received (₹)
                            </FormLabel>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={billSummary.RecivedAmt}
                              onChange={(e) => {
                                if (ispaid) return;
                                setBillSummary((prev) => ({
                                  ...prev,
                                  RecivedAmt: e.target.value,
                                  PendingAmt: parseFloat(
                                    (prev.netAmount - e.target.value).toFixed(2)
                                  ),
                                }));
                              }}
                            />
                          </FormControl>
                          {billSummary.RecivedAmt > 0 && (<><Flex align="center" justify="space-between" mt={3}>
                            <Text fontWeight="600">Upload payment receipt</Text>
                            <Button
                              size="sm"
                              colorScheme="blue"
                              onClick={() => {
                                const el =
                                  document.getElementById("receipt-file-input");
                                if (el) el.click();
                              }}
                            >
                              Add
                            </Button>
                          </Flex>

                            <Input
                              id="receipt-file-input"
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf"
                              multiple
                              style={{ display: "none" }}
                              onChange={async (e) => {
                                const files = Array.from(e.target.files || []);
                                if (files.length === 0) return;
                                const allowed = [
                                  "image/jpeg",
                                  "image/png",
                                  "application/pdf",
                                ];
                                const invalid = files.filter(
                                  (f) => !allowed.includes(f.type)
                                );
                                if (invalid.length > 0) {
                                  setReceiptError(
                                    "Please upload only JPG, PNG, or PDF files"
                                  );
                                  return;
                                }
                                setReceiptError("");
                                try {
                                  await uploadFilesAndSetUrls(files);
                                  // clear native input value so same file can be re-selected if removed
                                  e.target.value = "";
                                } catch (err) {
                                  console.error("Upload failed", err);
                                  toast({
                                    title: "Upload failed",
                                    description:
                                      err?.message || "Unable to upload files",
                                    status: "error",
                                    duration: 3000,
                                    isClosable: true,
                                  });
                                }
                              }}
                            />

                            <Box mt={3}>
                              {(() => {
                                const list = Array.isArray(receiptItems)
                                  ? receiptItems
                                  : [];
                                if (list.length === 0)
                                  return (
                                    <Text fontSize="sm" color="gray.500">
                                      No files uploaded
                                    </Text>
                                  );
                                return (
                                  <Box>
                                    <Text fontSize="sm" mb={2}>
                                      Uploaded file{list.length > 1 ? "s" : ""} (
                                      {list.length})
                                    </Text>
                                    <Box display="flex" flexWrap="wrap" gap={2}>
                                      {list.map((it, idx) => (
                                        <Tag
                                          key={idx}
                                          borderRadius="full"
                                          variant="subtle"
                                          colorScheme="gray"
                                        >
                                          <TagLabel maxW="200px" isTruncated>
                                            {it.name || it.url}
                                          </TagLabel>
                                          <button
                                            style={{
                                              backgroundColor: "#f0f0f0",
                                              color: "red",
                                              border: "none",
                                              borderRadius: "40%",
                                              cursor: "pointer",
                                              padding: "2px 6px",
                                              fontSize: "15px",
                                              marginLeft: "10px",
                                              fontWeight: "bold",
                                            }}
                                            size="xs"
                                            textColor={"red.700"}
                                            bg={"white"}
                                            className="hover:bg-red-600"
                                            onClick={() => removeReceiptItem(idx)}
                                          >
                                            X
                                          </button>
                                        </Tag>
                                      ))}
                                    </Box>
                                    <Flex gap={2} mt={2} align="center">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          setReceiptItems([]);
                                          setReceiptError("");
                                        }}
                                      >
                                        Clear All
                                      </Button>
                                    </Flex>
                                  </Box>
                                );
                              })()}
                            </Box></>)}


                          {ispending && (
                            <Box mt={3}>
                              <FormControl maxW="240px">
                                <FormLabel fontWeight="600" fontSize="14px">
                                  Pending Amount (₹)
                                </FormLabel>
                                <Text>{billSummary.PendingAmt}</Text>
                              </FormControl>
                            </Box>
                          )}
                        </Box>
                      )}
                    </VStack>
                  </ModalBody>
                  <ModalFooter>
                    <Button
                      variant="ghost"
                      mr={2}
                      onClick={() => setIsReceiptModalOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="solid"
                      mr={2}
                      onClick={async () => {
                        const list = Array.isArray(receiptItems)
                          ? receiptItems
                          : [];
                        if (billSummary.RecivedAmt > 0) {
                          if (list.length === 0) {
                            toast({
                              title: "No receipt uploaded",
                              description:
                                "Please upload atleast one receipt file.",
                              status: "warning",
                              duration: 3000,
                              isClosable: true,
                            });
                            return;
                          }
                        }
                        await CreateBill();
                        setIsBillViewOpen(true);
                      }}
                    >
                      Submit Bill
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme={
                appointmentData.isEmergency ||
                  appointmentData.isOutsideOPDEmergency
                  ? "red"
                  : "blue"
              }
              onClick={handleAddAppointment}
            >
              {appointmentData.isEmergency
                ? "Book Emergency Appointment"
                : appointmentData.isOutsideOPDEmergency
                  ? "Book Outside OPD Emergency Appointment"
                  : "Book Appointment"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Bill View Modal (shows printable bill and save options) */}
      <Modal
        isOpen={isBillViewOpen}
        // onClose={() => setIsBillViewOpen(false)}
        size="6xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent
          // Make modal take up most of the viewport and center the bill card
          w="90vw"
          h="90vh"
          maxW="90vw"
          maxH="90vh"
          p={0}
          m="auto"
          bg="#e6f7ff"
          borderRadius="md"
        >
          <ModalHeader
            position="sticky"
            top="0"
            zIndex={1}
            bg="#e6f7ff"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            py={3}
            px={4}
            borderBottom="1px solid rgba(0,0,0,0.06)"
          >
            <Text fontSize="lg" fontWeight="bold">
              Bill
            </Text>
            <Flex gap={2} alignItems="center">
              <Button
                variant="outline"
                onClick={async () => {
                  // Only save PDF, do not close
                  await generateBillPdf(
                    "bill-preview",
                    true,
                    `bill_${appointmentData.patientName || "patient"}.pdf`
                  );
                }}
              >
                Download Bill
              </Button>
              <Button
                colorScheme="blue"
                onClick={async () => {
                  try {
                    // await CreateBill();
                    await handleSaveBill();
                    // Close Bill view and booking modal
                    setIsBillViewOpen(false);
                    if (typeof onClose === "function") onClose();
                  } catch (e) {
                    // handleSaveBill shows toasts
                  }
                }}
              >
                Save Bill
              </Button>
              {/* <IconButton
                aria-label="Close bill"
                icon={<CloseIcon />}
                variant="ghost"
                onClick={() => setIsBillViewOpen(false)}
              />*/}
            </Flex>
          </ModalHeader>
          <ModalBody
            overflowY="auto"
            display="flex"
            justifyContent="center"
            alignItems="flex-start"
            p={6}
          >
            <Bill
              appointmentData={appointmentData}
              selectedServices={selectedServices}
              billSummary={billSummary}
              paymentState={paymentState}
              RecivedAmt={billSummary.RecivedAmt}
              pendingAmount={billSummary.PendingAmt}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default BookAppointmentModal;
